/**
 * MODULE: _shared/frontend-shell/src/hooks/assistant/utils.ts
 * PURPOSE: Utility functions for assistant hooks (localStorage, retry logic)
 * 
 * DEPENDENCIES: types.ts
 * 
 * PUBLIC API:
 *   - safeLocalStorageGet: Safe localStorage access with SSR guard
 *   - safeLocalStorageSet: Safe localStorage write with error handling
 *   - safeLocalStorageRemove: Safe localStorage removal
 *   - calculateBackoffDelay: Exponential backoff delay calculation
 *   - generateMessageId: Generate unique message ID with duplicate guard
 *   - getCrudCreateSubmitMessage: User-visible stream message for form submit by CRM entity
 *   - reviveAssistantMessageFromStorage: Parse JSON-hydrated messages (Dates, toolEvents)
 * 
 * SIDE EFFECTS:
 *   - localStorage access (guarded)
 * 
 * AGENT SAFETY: 🟢 SAFE - Utility functions
 * FILE SIZE: ~200 / 300 lines
 * 
 * Agent Notes:
 * - SSR-safe localStorage helpers
 * - Retry/backoff utilities
 * - Last updated: 2026-04-03 [2.2.269] — reviveAssistantMessageFromStorage (localStorage JSON → AssistantMessage).
 * - 2026-03-31 [2.2.228] — getCrudCreateSubmitMessage: stream user line for FormCard submit (account vs deal/opportunity labels).
 * 
 * See: /README.md | /ARCHITECTURE.md | useAssistantStream.ts
 */

import { DEFAULT_RETRY_CONFIG, type AssistantMessage, type RetryConfig } from './types';

/**
 * Safe localStorage get with SSR guard
 */
export function safeLocalStorageGet(key: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`Failed to read localStorage key "${key}":`, error);
    return null;
  }
}

/**
 * Safe localStorage set with error handling
 */
export function safeLocalStorageSet(key: string, value: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`Failed to write localStorage key "${key}":`, error);
    return false;
  }
}

/**
 * Safe localStorage remove
 */
export function safeLocalStorageRemove(key: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove localStorage key "${key}":`, error);
    return false;
  }
}

/**
 * Calculate exponential backoff delay
 * 
 * @param attempt Current retry attempt (0-indexed)
 * @param config Retry configuration
 * @returns Delay in milliseconds
 */
export function calculateBackoffDelay(
  attempt: number,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): number {
  const delay = Math.min(
    config.initialDelay * Math.pow(config.backoffMultiplier, attempt),
    config.maxDelay
  );
  
  // Add jitter (±20%) to prevent thundering herd
  const jitter = delay * 0.2 * (Math.random() * 2 - 1);
  return Math.max(0, delay + jitter);
}

/**
 * Generate unique message ID with duplicate guard
 * 
 * Uses crypto.randomUUID() with fallback to timestamp + random
 */
export function generateMessageId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for environments without crypto.randomUUID
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Check if error is retryable (network errors, 5xx, timeouts)
 */
export function isRetryableError(error: Error | unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true; // Network error
  }
  
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('failed to fetch') ||
      message.includes('500') ||
      message.includes('502') ||
      message.includes('503') ||
      message.includes('504')
    );
  }
  
  return false;
}

/**
 * Restore one message after JSON.parse(localStorage): ISO date strings → Date, tool event timestamps → Date.
 * Keeps uiComponents / dataResult / formRequest payloads intact for reload persistence.
 */
export function reviveAssistantMessageFromStorage(raw: unknown): AssistantMessage {
  const m = raw as AssistantMessage & { timestamp: string | Date | number };
  const out: AssistantMessage = {
    ...m,
    timestamp:
      m.timestamp instanceof Date
        ? m.timestamp
        : new Date(String(m.timestamp ?? Date.now())),
  };
  if (out.toolEvents?.length) {
    out.toolEvents = out.toolEvents.map((te) => ({
      ...te,
      started_at:
        te.started_at instanceof Date
          ? te.started_at
          : new Date(String(te.started_at ?? Date.now())),
      completed_at:
        te.completed_at == null
          ? undefined
          : te.completed_at instanceof Date
            ? te.completed_at
            : new Date(String(te.completed_at)),
    }));
  }
  return out;
}

/**
 * Stream message shown as the user turn when submitting an inline CRUD create form.
 * Must match continuation.entity from the assistant (e.g. account vs deal).
 */
export function getCrudCreateSubmitMessage(entity: string): string {
  const key = entity.trim().toLowerCase();
  if (key === 'deal' || key === 'opportunity') {
    return 'Create opportunity with submitted details';
  }
  if (key === 'account') {
    return 'Create Account with submitted details';
  }
  const label = key.length ? key.charAt(0).toUpperCase() + key.slice(1) : 'Record';
  return `Create ${label} with submitted details`;
}

/**
 * User-visible stream line when submitting the assistant form catalog (continuation.action === form_catalog).
 * Wording is descriptive; routing uses continuation, not this string.
 */
export function getFormCatalogSubmitMessage(continuation: {
  entity_slug?: string;
  mode?: string;
  formType?: string;
}): string {
  const raw = (continuation.entity_slug || continuation.formType || '').replace(/^(create|edit)_/, '');
  const words = raw.split('_').filter(Boolean);
  const label =
    words.length > 0
      ? words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
      : 'Record';
  const edit = (continuation.mode || '').toLowerCase() === 'edit';
  return edit
    ? `Update ${label} with submitted details`
    : `Create ${label} with submitted details`;
}
